import {Component, OnInit, OnDestroy} from '@angular/core';
import {User} from '../../user/user';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DbServerErrors} from "../../shared/interceptors/db-server-errors";
import {DbResponse} from "../../shared/interceptors/db-response";
import {NotifierComponent} from "../../shared/components/notifier/notifier.component";
import {Notification} from "../../shared/components/notifier/notification";
import {NotificationType} from "../../shared/components/notifier/notification-type";
import {AuthService} from "../../auth/auth.service";
import {SettingsResetPasswordComponent} from "../reset-password/settings-reset-password.component";
import {DialogService} from "../../../ng2-bootstrap-modal/dialog.service";
import {UserService} from '../../user/user.service';
import * as fromStore from "../../store";
import {combineLatest} from "rxjs";
import {filter, first, switchMap, takeUntil} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {Enable2faComponent} from "./enable-2fa/enable-2fa.component";
import {Disable2faComponent} from "./disable-2fa/disable-2fa.component";
import {Subscription} from "rxjs";
import {ChangeEmailComponent} from "../change-email/change-email.component";
import {Subject} from "rxjs";
import {UserSubscriptionStatus} from "../../user/user-subscription-status";
import * as workspaceActions from "../../store/actions/workspace.action";
import {DeleteAccountDialogComponent} from "./delete-account-dialog/delete-account-dialog.component";
import {UserSubscription, UserSubscriptionCategory} from "../../user/user-subscription";
import {clientRoles} from "../../user/user-roles-actions";
import {ActivatedRoute} from "@angular/router";
import {CompanyInfo} from "../../store";
import {BuilderSectionsInteractionService} from "../../builder-v2/builder-sections-interaction.service";

interface IDeleteAccountPermission {
  hasActiveWebsites: boolean;
  hasInvitedUsers: boolean;
  disabled: boolean;
}

@Component({
    selector: 'db-security-settings',
    templateUrl: 'security-settings.component.html',
    styleUrls: ['security-settings.component.scss'],
    standalone: false
})
export class SecuritySettingsComponent implements OnInit, OnDestroy {
  public loading: boolean;
  public securitySettings: FormGroup;
  public serverErrors: DbServerErrors;
  public user: User;
  private _subscriptions: Subscription[] = [];
  public accessGrant: boolean = false;
  public accessGrantRequest: boolean = true;
  public isMobile: boolean = false;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public isSubscriptionCanceled: boolean = false;
  public deleteAccountPermissions: IDeleteAccountPermission;
  public userSubscription: UserSubscription;
  public companyInfo: CompanyInfo;
  private _currentWorkspaceId: number;
  hasPayments = false;

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _userService: UserService,
    private _authService: AuthService,
    private _dialogService: DialogService,
    private _builderSectionsInteractionService: BuilderSectionsInteractionService,
    private _route: ActivatedRoute
  ) {
    this.securitySettings = new FormGroup({
      'email': new FormControl('', [Validators.required]),
    });
    this._subscriptions.push(this._store.select(fromStore.getWorkspaceIdEntities)
      .subscribe(workspaceIdEntities => {
        this._currentWorkspaceId = workspaceIdEntities.currentWorkspaceId;
      }));

    this.addEventHandlers();
  }

  ngOnInit(): void {
    this._getCompanyInfo();
    if (window.innerWidth < 991) {
      this.isMobile = true;
    }
  }

  private _getCompanyInfo(): void {
    this._builderSectionsInteractionService.companyInfo
        .pipe(
            filter(companyInfo => !!companyInfo),
            takeUntil(this._destroyStream$),
            first()
        )
        .subscribe(companyInfo => {
          this.companyInfo = companyInfo;
        });
  }

  private addEventHandlers(): void {
    this._store.dispatch(new workspaceActions.LoadWorkspaceAllClients());
    this._subscriptions.push(
      combineLatest(
        this._store.select(fromStore.getUserLoaded),
        this._store.select(fromStore.getUserLocalStorageLoaded),
      )
        .pipe(
          filter(([userLoaded, storageLoaded]) => userLoaded && storageLoaded),
          switchMap(() => combineLatest(
            this._store.select(fromStore.getUser),
            this._store.select(fromStore.getUserLocalStorage),
            this._store.select(fromStore.getUserSubscription),
            this._store.select(fromStore.getCurrentWorkspace),
            this._store.select(fromStore.getPayments)
          )),
          filter(([user, _]) => !!user),
          takeUntil(this._destroyStream$)
        )
        .subscribe(([user, userLocalStorage, userSubscription, workspace, payments]) => {
          this.hasPayments = payments && payments.find(payment => payment.paymentStatus === 'Confirmed');
          this.user = user;
          this.userSubscription = userSubscription;

          if (userSubscription) {
            this.isSubscriptionCanceled = workspace.roleId === clientRoles.OWNER &&
              (userSubscription.realStatus === UserSubscriptionStatus.canceled
                || this.userSubscription.type === UserSubscriptionCategory.STARTER);
          }

          if (this.isSubscriptionCanceled || userSubscription.isExpired) {
            this._getWebsites();
          }
          this.accessGrant = userLocalStorage && userLocalStorage.allow_grant_access || false;
          this.securitySettings.setValue(Object.assign(this.securitySettings.value, {
            'email': user.email,
          }));
        })
    );
  }


  ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
    this.removeEventHandlers();
  }

  private _getWebsites() {
    combineLatest(
      this._store.select(fromStore.getWebsitesLoaded),
      this._store.select(fromStore.getWorkspaceLoaded)
    )
      .pipe(
        takeUntil(this._destroyStream$),
        first(([websitesLoaded, workspaceLoaded]) => websitesLoaded && workspaceLoaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getAllWebsites),
          this._store.select(fromStore.getWorkspaceAllClients)
        )),
        first()
      )
      .subscribe(([websites, clients]) => {
        const hasInvitedUsers = !!clients.length;
        this.deleteAccountPermissions = {
          hasActiveWebsites: !!websites.length,
          hasInvitedUsers,
          disabled: !!websites.length || hasInvitedUsers
        };
      });
  }

  private removeEventHandlers(): void {
    this._subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
    this._subscriptions = null;
  }

  public onPasswordReset(): void {
    this._dialogService
      .addDialog(SettingsResetPasswordComponent, {
        user: this.user
      })
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.loading = true;
          const email = this.securitySettings.value.email;
          this._authService.sendResetPasswordMail(email)
            .pipe(filter((response: DbResponse): boolean => !!response),
            first())
            .subscribe((response: DbResponse): void => {
              this.loading = false;

              if (response.success) {
                NotifierComponent.notify(new Notification('Email with reset password url was sent to your mail.', NotificationType.INFO));
              } else {
                NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
              }
            });
        }
      });
  }

  public onEmailChange(): void {
    this._dialogService.addDialog(ChangeEmailComponent, {
      user: this.user
    });
  }

  public enable2fa(): void {
    this._dialogService
      .addDialog(Enable2faComponent, {
        userId: this.user && this.user.id
      })
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this._store.dispatch(new fromStore.LoadUser);
        }
      });
  }

  public disable2fa(): void {
    this._dialogService
      .addDialog(Disable2faComponent)
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this._store.dispatch(new fromStore.LoadUser);
        }
      });
  }

  public grantAccess(value) {
    this.accessGrant = value.currentTarget.checked;
    if (this.accessGrantRequest) {
      this.accessGrantRequest = false;

      this._userService.setUserGrantAccess(this.accessGrant)
        .pipe(filter((response: DbResponse) => !!response),
        first())
        .subscribe((response: DbResponse) => {
          if (response.success) {
            this._store.dispatch(new fromStore.LoadUserLocalStorage());
            NotifierComponent.notify(new Notification('Successfully saved', NotificationType.SUCCESS));
          } else {
            NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
          }
        });
    }
    setTimeout(() => {
      this.accessGrantRequest = true;
    }, 1000);
  }

  openDeleteAccountModal() {
    if (this.deleteAccountPermissions.disabled) {
      return;
    }
    this._dialogService.addDialog(DeleteAccountDialogComponent, {
      currentSubscription: this.userSubscription,
      user: this.user,
      currentWorkspaceId: this._currentWorkspaceId
    });
  }

}
